import { AlertTriangleIcon } from 'lucide-react'
import { Link } from 'react-router'

import { useBilling, useMe, useOpenInvoices } from '@/api/queries'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { hasBilling } from '@/lib/access'
import { centsLabel } from '@/lib/billing'
import { invoiceLabel, isPayable } from '@/lib/invoices'

/**
 * App-wide, for anyone with billing access: an open (unpaid) invoice means a payment failed. It
 * links to the invoice and to the cards. It claims nothing the server did not say; if the invoice
 * list cannot be read the banner stays out of the way.
 */
export function PastDueBanner() {
  const me = useMe()
  const allowed = hasBilling({ role: me.data?.role, billing: me.data?.billing })
  const billing = useBilling({ enabled: allowed })
  const live = allowed && billing.data?.billing_enabled === true && billing.data.has_customer
  const open = useOpenInvoices(live)

  const payable = (open.data ?? []).filter(isPayable)
  const first = payable[0]
  if (first === undefined) return null

  return (
    <Alert
      variant="warning"
      className="rounded-none border-x-0 border-t-0 px-6 lg:px-10"
      data-testid="past-due-banner"
    >
      <AlertTriangleIcon />
      <AlertTitle>Payment failed</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>
          Invoice {invoiceLabel(first)} ({centsLabel(first.amount_due_cents)})
          {payable.length > 1 ? ` and ${payable.length - 1} more` : ''} is unpaid. Pay it, or update your card,
          to keep your plan.
        </span>
        <span className="flex gap-2">
          <Button asChild size="sm">
            <Link to={`/app/invoices?open=${encodeURIComponent(first.id)}`}>Pay now</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/app/plan#payment-methods">Update card</Link>
          </Button>
        </span>
      </AlertDescription>
    </Alert>
  )
}
